import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button
} from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { StatusChip, type ChipTone } from '@/design';
import { ApiError, ProblemType } from '@/domains/api';
import { allowedDomainsText, useMailDelivery } from '@/domains/auth';
import type {
  IAdminParticipantDetail,
  IAdminParticipantInvitation,
  InvitationStatus
} from '../../api/participant-contracts';
import type { IOversightResponseRow } from '../../api/oversight-contracts';
import { participantsApi } from '../../api/participants-api';
import { relationWords } from '../../components/account-facts';
import { instantDay } from '../../components/disclosure-card';
import { responsePath } from '../../paths';
import { statusRefusal } from '../../refusal';
import {
  type ISaidNote,
  NOT_REREAD,
  NoteLine,
  OUT_OF_DATE,
  unanswered,
  unansweredNote
} from './said';

/**
 * A participant's invitations, as survey-api sends them on the hub's card — a point of contact's
 * (SR-416, its B5-16) and an assistant's an advisor sent (SR-487, `relation: "assistant"`, source
 * `respondent`) — drawn under Account & access, per household member holding any, newest first as
 * sent.
 *
 * **Drawn as sent, with one reading.** A TEAMS or RIA_FIRM submit naming an office email writes a
 * `pending` row and mails its link; the row stays `pending` past its expiry until a later submit
 * marks it `expired` (survey-api marks it lazily). So the status chip is the stored word, and a
 * `pending` row whose `expiresAt` has passed says beside it that its link expired — the queues'
 * Overdue precedent, the one reading made here. Every word is this module's; none mirrors C#.
 *
 * **Resend and Revoke on a `pending` row alone**, under `accounts.manage` (the section's
 * `NotPermitted` otherwise). Revoke is a confirmed press — no route undoes it. Resend is a plain
 * press: it mints a new link (the old one dies) and moves the expiry fourteen days on, which also
 * revives a row past its expiry. It carries no `deliverTo` — off production survey-api mails only
 * its allowlist, so a mail to any other address is dropped, which is said beside the answer where
 * the deployment redirects (never decided here by comparing hosts). **No route creates one.**
 *
 * The hub's write rules hold: said beside the press, held by the section per invitation id (so a
 * re-read moving the row's status keeps what was said), a success re-reads, a 409
 * `invitation-not-pending` and a 404 say the page was out of date and re-read, and an answer that
 * never arrived re-reads and asks to be checked.
 */
export function InvitationsList({
  member,
  multi,
  responses,
  notPermitted,
  notes,
  say,
  reread
}: {
  member: IAdminParticipantDetail;
  /** Whether the household has more than one member — the heading then names whose. */
  multi: boolean;
  /** The household's responses, to name the survey a point-of-contact answer came from. */
  responses: IOversightResponseRow[];
  /** The id of the section's `NotPermitted` sentence; present, every press is drawn off. */
  notPermitted?: string;
  notes: Record<string, IInvitationNote>;
  say: (invitation: IAdminParticipantInvitation, note: Nullable<ISaidNote>) => void;
  reread: () => Promise<boolean>;
}) {
  const invitations = member.invitations ?? [];
  if (invitations.length === 0) return null;

  return (
    <div className="grid gap-2" data-invitations={member.participantId}>
      <h4 className="text-sm font-semibold text-[var(--color-royal-blue)]">
        {multi ? `Invitations for ${member.displayName}` : 'Invitations'}
      </h4>
      <ul className="grid gap-2">
        {invitations.map((invitation) => (
          <InvitationRow
            key={invitation.invitationId}
            invitation={invitation}
            member={member}
            responses={responses}
            notPermitted={notPermitted}
            note={notes[invitation.invitationId]?.note ?? null}
            say={(note) => say(invitation, note)}
            reread={reread}
          />
        ))}
      </ul>
    </div>
  );
}

/** What an invitation's press was told, with its address — kept by the section per invitation id,
 *  so it can still be said under "Invitations no longer held here" once a re-read took the row
 *  away (a 404: the invitation is no longer the participant's). */
export interface IInvitationNote {
  email: string;
  note: ISaidNote;
}

/** What was said about invitations no longer on any member's card — the numbers' and the
 *  roster's precedent: a re-read must not take a refusal away with its row. */
export function InvitationsNoLongerHeld({
  notes,
  held
}: {
  notes: Record<string, IInvitationNote>;
  held: ReadonlySet<string>;
}) {
  const gone = Object.entries(notes).filter(([id]) => !held.has(id));
  if (gone.length === 0) return null;
  return (
    <div className="grid gap-2">
      <h4 className="text-sm font-semibold text-[var(--color-royal-blue)]">
        Invitations no longer held here
      </h4>
      <ul aria-label="Invitations no longer held here" className="grid gap-2">
        {gone.map(([id, entry]) => (
          <li key={id} className="grid gap-2 rounded-md border border-border px-3 py-2">
            <span className="font-medium break-all">{entry.email}</span>
            <NoteLine note={entry.note} />
          </li>
        ))}
      </ul>
    </div>
  );
}

/** A bodiless 403: the policy refusing a viewer whose roles no longer include the press. The
 *  forbidden handler re-reads `/auth/me`, which draws the section for the roles held now. */
const INVITATION_REFUSALS: Record<number, string> = {
  403:
    'The system refused this — your Team Portal roles no longer include managing accounts. The ' +
    'page is read again with your current roles.'
};

const STATUS: Record<string, { word: string; tone: ChipTone }> = {
  pending: { word: 'Pending', tone: 'attention' },
  accepted: { word: 'Accepted', tone: 'success' },
  superseded: { word: 'Superseded', tone: 'neutral' },
  revoked: { word: 'Revoked', tone: 'neutral' },
  expired: { word: 'Expired', tone: 'neutral' }
};

function statusOf(status: InvitationStatus): { word: string; tone: ChipTone } {
  return STATUS[status] ?? { word: status, tone: 'neutral' };
}

function InvitationRow({
  invitation,
  member,
  responses,
  notPermitted,
  note,
  say,
  reread
}: {
  invitation: IAdminParticipantInvitation;
  member: IAdminParticipantDetail;
  responses: IOversightResponseRow[];
  notPermitted?: string;
  note: Nullable<ISaidNote>;
  say: (note: Nullable<ISaidNote>) => void;
  reread: () => Promise<boolean>;
}) {
  const delivery = useMailDelivery();
  const [doing, setDoing] = React.useState<Nullable<'resend' | 'revoke'>>(null);
  const busy = doing !== null;
  const [confirming, setConfirming] = React.useState(false);
  const status = statusOf(invitation.status);
  const pending = invitation.status === 'pending';
  const lapsed = pending && Date.parse(invitation.expiresAt) < Date.now();
  const relation = relationWords(invitation.relation);

  const rowRef = React.useRef<HTMLLIElement>(null);
  // Whether the dialog closes on a press: read as it closes, which can be after the write has
  // finished — a state would be stale by then.
  const revokePressed = React.useRef(false);

  const write = async (kind: 'resend' | 'revoke') => {
    const refusedTitle = kind === 'resend' ? 'Not resent' : 'Not revoked';
    revokePressed.current = kind === 'revoke';
    setDoing(kind);
    say(null);
    let failure: unknown = null;
    try {
      if (kind === 'resend') {
        await participantsApi.resendInvitation(member.participantId, invitation.invitationId);
      } else {
        await participantsApi.revokeInvitation(member.participantId, invitation.invitationId);
      }
    } catch (error) {
      failure = error ?? new Error('No answer');
    }
    // Closed before the re-read: the re-read can take the row's Revoke — or the row — away, and a
    // dialog unmounted open drops the focus on the body (F8's Remove). Its close hands the focus
    // to the row instead (`onCloseAutoFocus`).
    setConfirming(false);
    try {
      if (failure === null) {
        const read = await reread();
        const text =
          kind === 'resend'
            ? `A new link is being sent to ${invitation.email}. Once it is sent, the old link ` +
              'stops working and the new one works for 14 days.' +
              (delivery?.redirect
                ? ` This environment mails only addresses at ${allowedDomainsText(
                    delivery.allowedDomains
                  )}; a mail to another address is dropped.`
                : '')
            : `Revoked the invitation to ${invitation.email}. Its link no longer works.`;
        say({ tone: 'done', text: text + (read ? '' : NOT_REREAD) });
      } else if (unanswered(failure)) {
        say(unansweredNote(await reread(), 'invitation'));
      } else if (
        failure instanceof ApiError &&
        (failure.status === 404 || failure.problem?.type === ProblemType.invitationNotPending)
      ) {
        const read = await reread();
        say({
          tone: 'refused',
          title: refusedTitle,
          text:
            (failure.status === 404
              ? `The invitation to ${invitation.email} is no longer this participant's — `
              : `The invitation to ${invitation.email} is no longer pending — `) +
            'the page was out of date. ' +
            (read ? 'It has been read again.' : OUT_OF_DATE)
        });
      } else {
        say({
          tone: 'refused',
          title: refusedTitle,
          text: statusRefusal(failure, INVITATION_REFUSALS, 'Could not change the invitation')
        });
      }
    } finally {
      setDoing(null);
    }
  };

  return (
    <li
      ref={rowRef}
      tabIndex={-1}
      className="grid gap-2 rounded-md border border-border px-3 py-2"
      data-invitation={invitation.invitationId}
      data-status={invitation.status}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium break-all">{invitation.email}</span>
        {relation && <span className="text-sm text-muted-foreground">{relation}</span>}
        <StatusChip tone={status.tone}>{status.word}</StatusChip>
        {lapsed && (
          <span className="text-sm text-muted-foreground" data-lapsed>
            Its link expired {instantDay(invitation.expiresAt)}
          </span>
        )}
      </div>
      <p className="text-sm text-muted-foreground">
        <SourceWords invitation={invitation} responses={responses} />
        {' · '}Sent {instantDay(invitation.createdAt)}
        {datesWords(invitation, pending, lapsed)}
        {invitation.status === 'accepted' && invitation.accountId && (
          <> · Accepted by {acceptedBy(member, invitation.accountId)}</>
        )}
      </p>
      {pending && (
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={busy || notPermitted !== undefined}
            aria-describedby={notPermitted}
            aria-label={`Resend the invitation to ${invitation.email}`}
            onClick={() => void write('resend')}
          >
            {doing === 'resend' ? 'Sending…' : 'Resend'}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={busy || notPermitted !== undefined}
            aria-describedby={notPermitted}
            aria-label={`Revoke the invitation to ${invitation.email}`}
            onClick={() => setConfirming(true)}
          >
            Revoke
          </Button>
        </div>
      )}
      {note && <NoteLine note={note} />}
      {/* Mounted whatever the row's status, outside its `pending` presses: the re-read after a
          revocation takes those away, and a dialog unmounted open drops the focus on the body. */}
      <AlertDialog open={confirming} onOpenChange={(open) => !busy && setConfirming(open)}>
        <AlertDialogContent
          onCloseAutoFocus={(event) => {
            // Closed by a press, the focus goes to the row, whose Revoke the re-read is about to
            // take away; closed by Cancel or Esc, Radix hands it back to Revoke.
            if (revokePressed.current) {
              revokePressed.current = false;
              event.preventDefault();
              rowRef.current?.focus();
            }
          }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke the invitation to {invitation.email}?</AlertDialogTitle>
            <AlertDialogDescription>The link stops working. No mail is sent.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={busy}
              onClick={(event) => {
                event.preventDefault();
                void write('revoke');
              }}
            >
              {doing === 'revoke' ? 'Revoking…' : 'Revoke'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </li>
  );
}

/** Where the invitation came from, in words — a point-of-contact answer linked to its response;
 *  an advisor's invitation of an assistant (SR-487) is the advisor's. */
function SourceWords({
  invitation,
  responses
}: {
  invitation: IAdminParticipantInvitation;
  responses: IOversightResponseRow[];
}) {
  if (invitation.source === 'staff') return <>Invited by Veritant</>;
  if (invitation.source === 'respondent') return <>Invited by the advisor</>;
  if (invitation.source !== 'poc_answer') return <>{invitation.source}</>;
  if (!invitation.responseId) return <>Named in a response</>;
  const survey = responses.find((row) => row.responseId === invitation.responseId)?.survey.name;
  return (
    <Link
      to={responsePath(invitation.responseId)}
      className="text-[var(--color-sapphire)] underline-offset-4 hover:underline"
    >
      {survey ? `Named in the ${survey} response` : 'Named in a response'}
    </Link>
  );
}

/** The day a decided invitation was decided, else a live pending one's expiry, else nothing. */
function datesWords(
  invitation: IAdminParticipantInvitation,
  pending: boolean,
  lapsed: boolean
): string {
  if (invitation.decidedAt) return ` · Decided ${instantDay(invitation.decidedAt)}`;
  if (pending && !lapsed) return ` · Expires ${instantDay(invitation.expiresAt)}`;
  return '';
}

/** The account an acceptance linked — by its address where the card carries it, else its id. */
function acceptedBy(member: IAdminParticipantDetail, accountId: string): string {
  return member.accounts.find((account) => account.accountId === accountId)?.email ?? accountId;
}
